import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  afterRenderEffect,
  inject,
  input,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { UI_TEXT } from '../i18n';

let nextId = 0;

/** Medida CSS: un número es px; sin valor, sin medida. */
const toCss = (value: string | number | null | undefined) =>
  typeof value === 'number' ? `${value}px` : (value ?? undefined);

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Ventana modal del kit (reemplaza a dx-popup). La controla la pantalla: se ve mientras `open` es
 * verdadero. La × y Escape no la cierran solas: avisan con `(closed)` y la pantalla decide (pone su
 * estado en null, o confirma antes si hay cambios sin guardar).
 *
 * Como la de DevExtreme: el foco entra al panel al abrirse y no sale con Tab, un clic fuera no la
 * cierra, Escape la cierra sólo si se puede cerrar (`closable`; mientras guarda, no) y sólo a la de
 * arriba si hay dos. Además, al cerrarse el foco vuelve a quien la abrió.
 */
@Component({
  selector: 'sc-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-template #panel>
      <div
        class="sck-dialog"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        [attr.aria-labelledby]="titleId"
      >
        <div class="sck-dialog__title">
          <div class="sck-dialog__title-text" [id]="titleId">{{ title() }}</div>
          @if (showCloseButton() ?? closable()) {
            <button
              type="button"
              class="sck-dialog__close"
              [attr.aria-label]="text.close"
              (click)="closed.emit()"
            >
              <span class="material-icons sck-icon" aria-hidden="true">close</span>
            </button>
          }
        </div>
        <div class="sck-dialog__body"><ng-content /></div>
      </div>
    </ng-template>
  `,
})
export class ScDialogComponent implements OnDestroy {
  readonly open = input(false);
  readonly title = input('');
  /** Muestra la × y deja cerrar con Escape (falso mientras se guarda). */
  readonly closable = input(true);
  /** Muestra la ×; sin valor, sigue a `closable` (la confirmación no la tiene, pero Escape cierra). */
  readonly showCloseButton = input<boolean | null>(null);
  /** Dónde queda el foco al abrir: el panel (como dx-popup) o su primer botón o campo. */
  readonly initialFocus = input<'panel' | 'first'>('panel');
  /** Ancho: número en px o cualquier medida CSS; sin valor, el del contenido. */
  readonly width = input<string | number | null>(null);
  /** Alto fijo (número en px o medida CSS); sin valor, el del contenido hasta `maxHeight`. */
  readonly height = input<string | number | null>(null);
  /** Máximos: número en px o medida CSS (por defecto los de dx-popup en el BackOffice). */
  readonly maxWidth = input<string | number>('92vw');
  readonly maxHeight = input<string | number>('90%');
  /** Clase extra del panel (p. ej. `sc-popup`, para las reglas de formulario de la ventana). */
  readonly panelClass = input<string | null>(null);
  readonly closed = output<void>();

  protected readonly text = UI_TEXT;
  protected readonly titleId = `sck-dialog-title-${++nextId}`;

  private readonly overlay = inject(Overlay);
  private readonly viewContainer = inject(ViewContainerRef);
  // La configurable, no la básica: si el foco igual sale (un clic en el fondo y Shift+Tab), lo
  // devuelve; y con dos ventanas, sólo la de arriba atrapa.
  private readonly focusTraps = inject(ConfigurableFocusTrapFactory);
  private readonly document = inject(DOCUMENT);
  private readonly panel = viewChild.required<TemplateRef<unknown>>('panel');

  private overlayRef: OverlayRef | null = null;
  private focusTrap: ConfigurableFocusTrap | null = null;
  private opener: HTMLElement | null = null;
  private resize: ResizeObserver | null = null;
  private recenter: (() => void) | null = null;

  constructor() {
    afterRenderEffect(() => {
      const open = this.open();
      untracked(() => (open ? this.show() : this.hide()));
    });
  }

  ngOnDestroy(): void {
    this.hide();
  }

  private show(): void {
    if (this.overlayRef) return;
    this.opener = this.document.activeElement as HTMLElement | null;
    const width = this.width();
    const height = this.height();
    const extra = this.panelClass();
    const ref = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'sck-dialog-backdrop',
      panelClass: extra ? ['sck-dialog-pane', extra] : ['sck-dialog-pane'],
      width: toCss(width),
      height: toCss(height),
      maxWidth: toCss(this.maxWidth()),
      maxHeight: toCss(this.maxHeight()),
      positionStrategy: this.overlay.position().global(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      // Fuera de la capa superior del navegador (la que usa el CDK por omisión): ahí ningún z-index
      // le gana, y las listas desplegables y los avisos que se abren sobre la ventana quedarían debajo.
      usePopover: false,
    });
    ref.attach(new TemplatePortal(this.panel(), this.viewContainer));
    // El despachador del CDK manda el teclado sólo a la capa de más arriba.
    ref.keydownEvents().subscribe((event) => {
      // Si ya lo atendió otro (una lista abierta adentro lo marca), era para cerrar esa lista.
      if (event.key !== 'Escape' || event.defaultPrevented || !this.closable()) return;
      event.preventDefault();
      this.closed.emit();
    });
    this.overlayRef = ref;
    // Centrada en píxeles enteros, como dx-popup (el CDK la centraría con decimales y el borde se
    // dibujaría entre dos píxeles); se vuelve a centrar si cambia el tamaño de la ventana o del panel.
    const center = () => {
      const box = ref.overlayElement.getBoundingClientRect();
      const view = this.document.defaultView;
      if (!view) return;
      const left = Math.max(0, Math.round((view.innerWidth - box.width) / 2));
      const top = Math.max(0, Math.round((view.innerHeight - box.height) / 2));
      ref.updatePositionStrategy(
        this.overlay.position().global().left(`${left}px`).top(`${top}px`),
      );
    };
    center();
    this.resize = new ResizeObserver(center);
    this.resize.observe(ref.overlayElement);
    this.document.defaultView?.addEventListener('resize', center);
    this.recenter = center;
    const element = ref.overlayElement.querySelector<HTMLElement>('.sck-dialog');
    if (element) {
      this.focusTrap = this.focusTraps.create(element);
      // «first»: el primer botón o campo del contenido. El panel recibe el foco por código
      // (tabindex -1) pero Tab no lo recorre: así la vuelta llega al último control.
      const first =
        this.initialFocus() === 'first' ? element.querySelector<HTMLElement>(FOCUSABLE) : null;
      (first ?? element).focus();
    }
  }

  private hide(): void {
    if (!this.overlayRef) return;
    this.focusTrap?.destroy();
    this.resize?.disconnect();
    this.resize = null;
    if (this.recenter) this.document.defaultView?.removeEventListener('resize', this.recenter);
    this.recenter = null;
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.focusTrap = null;
    const opener = this.opener;
    this.opener = null;
    if (opener?.isConnected) opener.focus();
  }
}
